import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Gift, Loader2, Package, User, Phone, MapPin, Calendar, IndianRupee,
  CheckCircle2, AlertTriangle, RotateCcw, Banknote, Shield, Settings2,
} from "lucide-react";

const STATUS_META = {
  issued:    { label: "Active (within retention)", tone: "text-orange-700 bg-orange-50 border-orange-200", icon: Gift },
  retained:  { label: "Retained (90+ days)",      tone: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: CheckCircle2 },
  returned:  { label: "Returned",                  tone: "text-slate-600 bg-slate-100 border-slate-200", icon: RotateCcw },
  recovered: { label: "Cash recovered",            tone: "text-red-600 bg-red-50 border-red-200", icon: Banknote },
};

function fmtDate(s) {
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
}

function daysLeft(kit) {
  if (!kit.must_retain_until) return null;
  const ms = new Date(kit.must_retain_until).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

function ActionDialog({ open, onOpenChange, kit, mode, onDone }) {
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && kit) { setNote(""); setAmount(String(kit.total_value || "")); }
  }, [open, kit]);

  if (!kit) return null;
  const isRecover = mode === "recovered";

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { note };
      if (isRecover) {
        const a = parseFloat(amount);
        if (!a || a <= 0) { setSubmitting(false); return toast.error("Enter the recovered amount (INR)"); }
        payload.recovery_amount = a;
      }
      await api.post(`/agent/welcome-kit/admin/${kit.agent_id}/${mode}`, payload);
      toast.success(isRecover ? "Kit value recovered" : "Kit marked as returned");
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={`kit-action-dialog-${mode}`}>
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg flex items-center gap-2">
            {isRecover ? <><Banknote className="h-5 w-5 text-red-500" /> Recover Kit Value</>
                       : <><RotateCcw className="h-5 w-5 text-slate-500" /> Mark Kit Returned</>}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {isRecover ? "Record cash/deposit recovery when an agent leaves before retention." : "Confirm that the agent has handed back the full kit."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
            <div><span className="font-semibold text-slate-700">Agent:</span> {kit.agent?.name || "—"} {kit.agent?.phone && `· ${kit.agent.phone}`}</div>
            <div><span className="font-semibold text-slate-700">Kit value:</span> ₹{kit.total_value}</div>
            <div><span className="font-semibold text-slate-700">Issued:</span> {fmtDate(kit.issued_at)}</div>
          </div>
          {isRecover && (
            <div>
              <Label className="text-xs font-semibold text-slate-700">Amount recovered (INR)</Label>
              <Input
                type="number" min="1" step="1"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                className="rounded-xl h-10 mt-1" data-testid="recover-amount-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">Defaults to full kit value. Adjust if partial recovery.</p>
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold text-slate-700">Note (optional)</Label>
            <Input
              placeholder={isRecover ? "e.g. deducted from security deposit" : "e.g. walked into office 2026-04-20"}
              value={note} onChange={(e) => setNote(e.target.value)}
              className="rounded-xl h-10 mt-1" data-testid={`${mode}-note-input`}
            />
          </div>
          <Button
            onClick={submit} disabled={submitting}
            className={`w-full rounded-xl h-11 ${isRecover ? "bg-red-600 hover:bg-red-700" : "bg-slate-700 hover:bg-slate-800"}`}
            data-testid={`submit-${mode}-btn`}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRecover ? "Confirm Recovery" : "Confirm Returned")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KitConfigDialog({ open, onOpenChange, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/agent/welcome-kit/config")
      .then(({ data }) => setCfg(data))
      .catch((err) => toast.error(err.response?.data?.detail || "Couldn't load config"))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const updateItem = (i, patch) => setCfg((c) => {
    const items = [...(c.items || [])];
    items[i] = { ...items[i], ...patch };
    return { ...c, items };
  });

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const payload = {
        items: (cfg.items || []).map((i) => ({
          id: i.id, name: i.name, emoji: i.emoji || "", value: parseInt(i.value) || 0,
        })),
        retention_days: parseInt(cfg.retention_days) || 90,
        insurance_cover: parseInt(cfg.insurance_cover) || 200000,
        insurance_partner: cfg.insurance_partner || "",
      };
      const { data } = await api.put("/agent/welcome-kit/config", payload);
      setCfg(data);
      toast.success("Config saved");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kit-config-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-[#FF6B00]" /> Welcome Kit Config
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Edit items, retention period and insurance cover. Existing kits keep their original snapshot.
          </DialogDescription>
        </DialogHeader>
        {loading || !cfg ? (
          <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-700">Retention days</Label>
                <Input
                  type="number" min="0" value={cfg.retention_days}
                  onChange={(e) => setCfg({ ...cfg, retention_days: e.target.value })}
                  className="rounded-xl h-10 mt-1" data-testid="cfg-retention-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Insurance cover (INR)</Label>
                <Input
                  type="number" min="0" step="10000" value={cfg.insurance_cover}
                  onChange={(e) => setCfg({ ...cfg, insurance_cover: e.target.value })}
                  className="rounded-xl h-10 mt-1" data-testid="cfg-insurance-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Insurance partner note</Label>
              <Input
                value={cfg.insurance_partner || ""}
                onChange={(e) => setCfg({ ...cfg, insurance_partner: e.target.value })}
                className="rounded-xl h-10 mt-1" data-testid="cfg-partner-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-slate-700">Kit items</Label>
                <div className="text-[10px] text-slate-500">Total ₹{(cfg.items || []).reduce((s, i) => s + (parseInt(i.value) || 0), 0)}</div>
              </div>
              <div className="space-y-2">
                {(cfg.items || []).map((it, i) => (
                  <div key={it.id || `item-${i}`} className="grid grid-cols-[40px_1fr_100px_80px] gap-2 items-center bg-slate-50 rounded-lg p-2" data-testid={`cfg-item-${it.id}`}>
                    <Input value={it.emoji || ""} onChange={(e) => updateItem(i, { emoji: e.target.value })} className="text-center rounded-lg h-9 text-lg" maxLength={3} />
                    <Input value={it.name} onChange={(e) => updateItem(i, { name: e.target.value })} className="rounded-lg h-9" />
                    <Input readOnly value={it.id} className="rounded-lg h-9 bg-slate-100 text-xs font-mono text-slate-500" />
                    <Input type="number" min="0" value={it.value} onChange={(e) => updateItem(i, { value: e.target.value })} className="rounded-lg h-9 text-right" />
                  </div>
                ))}
              </div>
            </div>
            <Button
              onClick={save} disabled={saving}
              className="w-full bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11"
              data-testid="save-kit-config-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Config"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminWelcomeKits() {
  const [statusFilter, setStatusFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState({ kit: null, mode: null }); // "returned" | "recovered"
  const [cfgOpen, setCfgOpen] = useState(false);

  const fetchKits = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter === "all"
        ? "/agent/welcome-kit/admin/all"
        : `/agent/welcome-kit/admin/all?status=${statusFilter}`;
      const { data } = await api.get(url);
      setRows(data.kits || []);
      setSummary(data.summary || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load kits");
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchKits(); }, [fetchKits]);

  return (
    <div className="space-y-6 animate-fadeInUp" data-testid="admin-welcome-kits-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Gift className="h-7 w-7 text-[#FF6B00]" /> Welcome Kits
          </h1>
          <p className="text-sm text-slate-500 mt-1">Track issued kits. Mark returned when agent hands it back, or recover cash value if they leave before retention.</p>
        </div>
        <Button onClick={() => setCfgOpen(true)} variant="outline" className="rounded-xl h-10" data-testid="open-kit-config-btn">
          <Settings2 className="h-4 w-4 mr-1.5" /> Kit Config
        </Button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="kit-summary-stats">
        {[
          { k: "issued",    label: "Active",    color: "text-orange-700 bg-orange-50 border-orange-200", icon: Gift },
          { k: "retained",  label: "Retained",  color: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: Shield },
          { k: "returned",  label: "Returned",  color: "text-slate-700 bg-slate-50 border-slate-200", icon: RotateCcw },
          { k: "recovered", label: "Recovered", color: "text-red-700 bg-red-50 border-red-200", icon: Banknote },
        ].map((s) => (
          <div key={s.k} className={`rounded-2xl border p-4 ${s.color}`} data-testid={`kit-stat-${s.k}`}>
            <div className="flex items-center gap-2">
              <s.icon className="h-4 w-4" />
              <span className="text-[11px] font-bold uppercase tracking-wider">{s.label}</span>
            </div>
            <div className="font-['Outfit'] text-3xl font-extrabold mt-1">{summary[s.k] || 0}</div>
          </div>
        ))}
      </div>

      {/* Active Kit Value */}
      <div className="rounded-2xl bg-gradient-to-r from-[#FF6B00]/10 via-amber-50 to-white border border-orange-200 p-4 flex items-center gap-3" data-testid="kit-total-value-card">
        <div className="w-11 h-11 rounded-xl bg-[#FF6B00] text-white flex items-center justify-center">
          <IndianRupee className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Kit value on the street</div>
          <div className="font-['Outfit'] text-2xl font-extrabold text-slate-900">₹{(summary.active_kit_value || 0).toLocaleString("en-IN")}</div>
        </div>
        <div className="text-[10px] text-slate-400 hidden sm:block max-w-xs">
          Total value of kits currently with active+retained agents. Recover this amount when agents leave before retention.
        </div>
      </div>

      {/* Filter tabs */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList className="bg-slate-100 rounded-xl" data-testid="kit-filter-tabs">
          <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
          <TabsTrigger value="issued" data-testid="filter-issued">Active</TabsTrigger>
          <TabsTrigger value="retained" data-testid="filter-retained">Retained</TabsTrigger>
          <TabsTrigger value="returned" data-testid="filter-returned">Returned</TabsTrigger>
          <TabsTrigger value="recovered" data-testid="filter-recovered">Recovered</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Kits table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400 flex flex-col items-center gap-2">
            <Package className="h-8 w-8" />
            No kits {statusFilter !== "all" ? `with status "${statusFilter}"` : "issued yet"}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Agent</th>
                  <th className="px-4 py-3 text-left hidden md:table-cell">Contact</th>
                  <th className="px-4 py-3 text-left">Issued</th>
                  <th className="px-4 py-3 text-left">Retention</th>
                  <th className="px-4 py-3 text-right">Value</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((k) => {
                  const meta = STATUS_META[k.status] || STATUS_META.issued;
                  const Icon = meta.icon;
                  const left = daysLeft(k);
                  const canAct = k.status === "issued" || k.status === "retained";
                  return (
                    <tr key={k._id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`kit-row-${k._id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{k.agent?.name || <span className="text-slate-400">Unknown</span>}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{k.agent_id.slice(0, 8)}…</div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="text-xs text-slate-600 flex items-center gap-1"><Phone className="h-3 w-3" /> {k.agent?.phone || "—"}</div>
                        <div className="text-[10px] text-slate-400 flex items-center gap-1 line-clamp-1"><MapPin className="h-3 w-3" /> {k.agent?.address || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        <div className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(k.issued_at)}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {k.status === "retained" ? (
                          <span className="text-emerald-600 font-semibold">Complete</span>
                        ) : (left !== null ? (
                          <span className={left <= 15 ? "text-amber-600 font-semibold" : "text-slate-600"}>
                            {left} days left
                          </span>
                        ) : "—")}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-slate-900">₹{k.total_value}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border ${meta.tone}`}>
                          <Icon className="h-3 w-3" /> {k.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canAct ? (
                          <div className="flex justify-end gap-1.5">
                            <Button
                              size="sm" variant="outline"
                              className="rounded-lg h-8 text-xs"
                              onClick={() => setAction({ kit: k, mode: "returned" })}
                              data-testid={`mark-returned-${k._id}`}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" /> Returned
                            </Button>
                            <Button
                              size="sm"
                              className="rounded-lg h-8 text-xs bg-red-600 hover:bg-red-700 text-white"
                              onClick={() => setAction({ kit: k, mode: "recovered" })}
                              data-testid={`mark-recovered-${k._id}`}
                            >
                              <Banknote className="h-3 w-3 mr-1" /> Recover
                            </Button>
                          </div>
                        ) : (
                          <span className="text-[10px] text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ActionDialog
        open={!!action.mode}
        onOpenChange={(o) => { if (!o) setAction({ kit: null, mode: null }); }}
        kit={action.kit}
        mode={action.mode}
        onDone={fetchKits}
      />

      <KitConfigDialog open={cfgOpen} onOpenChange={setCfgOpen} onSaved={fetchKits} />
    </div>
  );
}
